import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { useProfile } from '../context/ProfileContext';
import { supabase } from '../supabaseClient';

const { FiBell, FiSearch, FiX, FiCheckCircle, FiInfo, FiAlertCircle, FiLogOut, FiBriefcase, FiZap, FiFileText } = FiIcons;

const Header = () => {
  const navigate = useNavigate();
  const [showNotifications, setShowNotifications] = useState(false);
  const { session } = useAuth();
  const { data } = useProfile();

  const [searchQuery, setSearchQuery] = useState('');
  const [isSearchFocused, setIsSearchFocused] = useState(false);
  const searchRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setIsSearchFocused(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const getSearchResults = () => {
    if (!searchQuery.trim() || !data) return null;

    const query = searchQuery.toLowerCase();
    const results = {
      applications: (data.applications || []).filter(app =>
        (app.company && app.company.toLowerCase().includes(query)) ||
        (app.role && app.role.toLowerCase().includes(query))
      ),
      experience: (data.user?.experience || []).filter(exp =>
        (exp.company && exp.company.toLowerCase().includes(query)) ||
        (exp.role && exp.role.toLowerCase().includes(query))
      ),
      skills: (data.user?.skills || []).filter(skill =>
        skill.toLowerCase().includes(query)
      ),
      jobAlerts: (data.jobAlerts || []).filter(alert =>
        (alert.title && alert.title.toLowerCase().includes(query)) ||
        (alert.company && alert.company.toLowerCase().includes(query))
      )
    };

    const hasResults = Object.values(results).some(arr => arr.length > 0);
    if (!hasResults) return null;

    return results;
  };

  const searchResults = getSearchResults();

  const handleSearchResultClick = (path) => {
    navigate(path);
    setIsSearchFocused(false);
    setSearchQuery('');
  };

  const notifications = [];
  const unreadCount = notifications.filter(n => !n.read).length;

  const handleLogout = async () => {
    await supabase.auth.signOut();
  };

  const getNotifyIcon = (type) => {
    switch(type) {
      case 'status': return <FiCheckCircle className="text-green-500" />;
      case 'review': return <FiInfo className="text-blue-500" />;
      case 'alert': return <FiAlertCircle className="text-orange-500" />;
      default: return <FiBell />;
    }
  };

  return (
    <header className="h-20 bg-white/80 backdrop-blur-md border-b border-gray-100 flex items-center justify-between px-8 sticky top-0 z-30">
      <div className="relative z-50" ref={searchRef}>
        <div className="flex items-center bg-gray-100 rounded-full px-4 py-2 w-96 focus-within:ring-2 focus-within:ring-blue-500 transition-all">
          <SafeIcon icon={FiSearch} className="text-gray-400 mr-2" />
          <input
            type="text"
            placeholder="Search jobs, resumes, or keywords..."
            className="bg-transparent border-none outline-none w-full text-sm text-gray-700"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onFocus={() => setIsSearchFocused(true)}
          />
          {searchQuery && (
            <button onClick={() => setSearchQuery('')} className="text-gray-400 hover:text-gray-600 transition-colors">
               <SafeIcon icon={FiX} />
            </button>
          )}
        </div>

        <AnimatePresence>
          {isSearchFocused && searchQuery.trim() && (
            <motion.div
              initial={{ opacity: 0, y: 10, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 10, scale: 0.95 }}
              className="absolute left-0 mt-3 w-96 bg-white rounded-2xl shadow-2xl border border-gray-100 z-50 overflow-hidden"
            >
              <div className="max-h-[400px] overflow-y-auto p-2">
                {!searchResults ? (
                  <div className="p-4 text-center text-sm text-gray-500">
                    No results found for "{searchQuery}"
                  </div>
                ) : (
                  <>
                    {searchResults.applications?.length > 0 && (
                      <div className="mb-2">
                        <h4 className="text-[10px] font-black uppercase text-gray-400 px-3 py-1">Applications</h4>
                        {searchResults.applications.map(app => (
                          <div
                            key={`app-${app.id}`}
                            onClick={() => handleSearchResultClick('/tracker')}
                            className="px-3 py-2 hover:bg-gray-50 rounded-xl cursor-pointer transition-colors flex items-center gap-3"
                          >
                             <div className="p-2 bg-blue-50 text-blue-600 rounded-lg">
                               <SafeIcon icon={FiBriefcase} />
                             </div>
                             <div>
                               <p className="text-sm font-bold text-gray-900">{app.company}</p>
                               <p className="text-xs text-gray-500">{app.role}</p>
                             </div>
                          </div>
                        ))}
                      </div>
                    )}

                    {searchResults.experience?.length > 0 && (
                      <div className="mb-2">
                        <h4 className="text-[10px] font-black uppercase text-gray-400 px-3 py-1">Experience</h4>
                        {searchResults.experience.map((exp, idx) => (
                          <div
                            key={`exp-${idx}`}
                            onClick={() => handleSearchResultClick('/base-resume')}
                            className="px-3 py-2 hover:bg-gray-50 rounded-xl cursor-pointer transition-colors flex items-center gap-3"
                          >
                             <div className="p-2 bg-purple-50 text-purple-600 rounded-lg">
                               <SafeIcon icon={FiFileText} />
                             </div>
                             <div>
                               <p className="text-sm font-bold text-gray-900">{exp.company}</p>
                               <p className="text-xs text-gray-500">{exp.role}</p>
                             </div>
                          </div>
                        ))}
                      </div>
                    )}

                    {searchResults.jobAlerts?.length > 0 && (
                      <div className="mb-2">
                        <h4 className="text-[10px] font-black uppercase text-gray-400 px-3 py-1">Job Alerts</h4>
                        {searchResults.jobAlerts.map(alert => (
                          <div
                            key={`alert-${alert.id}`}
                            onClick={() => handleSearchResultClick('/alerts')}
                            className="px-3 py-2 hover:bg-gray-50 rounded-xl cursor-pointer transition-colors flex items-center gap-3"
                          >
                             <div className="p-2 bg-orange-50 text-orange-600 rounded-lg">
                               <SafeIcon icon={FiBell} />
                             </div>
                             <div>
                               <p className="text-sm font-bold text-gray-900">{alert.title}</p>
                               <p className="text-xs text-gray-500">{alert.company}</p>
                             </div>
                          </div>
                        ))}
                      </div>
                    )}

                    {searchResults.skills?.length > 0 && (
                      <div className="mb-2">
                        <h4 className="text-[10px] font-black uppercase text-gray-400 px-3 py-1">Skills</h4>
                        <div className="px-3 py-1 flex flex-wrap gap-1">
                          {searchResults.skills.map((skill, idx) => (
                            <span
                              key={`skill-${idx}`}
                              onClick={() => handleSearchResultClick('/base-resume')}
                              className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded-md cursor-pointer hover:bg-gray-200 transition-colors"
                            >
                              {skill}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      <div className="flex items-center gap-4">
        <div className="relative">
          <button 
            onClick={() => setShowNotifications(!showNotifications)}
            className="relative p-2 text-gray-400 hover:text-gray-600 transition-colors bg-gray-50 rounded-lg"
          >
            <SafeIcon icon={FiBell} className="text-xl" />
            {unreadCount > 0 && (
              <span className="absolute top-1 right-1 w-4 h-4 bg-red-500 rounded-full border-2 border-white text-[10px] text-white flex items-center justify-center font-bold">
                {unreadCount}
              </span>
            )}
          </button>

          <AnimatePresence>
            {showNotifications && (
              <>
                <div 
                  className="fixed inset-0 z-40" 
                  onClick={() => setShowNotifications(false)}
                ></div>
                <motion.div 
                  initial={{ opacity: 0, y: 10, scale: 0.95 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: 10, scale: 0.95 }}
                  className="absolute right-0 mt-3 w-80 bg-white rounded-2xl shadow-2xl border border-gray-100 z-50 overflow-hidden"
                >
                  <div className="p-4 border-b border-gray-50 flex justify-between items-center bg-gray-50/50">
                    <h3 className="font-bold text-gray-900">Notifications</h3>
                    <button onClick={() => setShowNotifications(false)}>
                      <SafeIcon icon={FiX} className="text-gray-400 hover:text-gray-600" />
                    </button>
                  </div>
                  <div className="max-h-[400px] overflow-y-auto">
                    {notifications.map((n) => (
                      <div 
                        key={n.id} 
                        className={`p-4 border-b border-gray-50 hover:bg-gray-50 transition-colors cursor-pointer ${!n.read ? 'bg-blue-50/30' : ''}`}
                      >
                        <div className="flex gap-3">
                          <div className="mt-1">{getNotifyIcon(n.type)}</div>
                          <div>
                            <p className="text-sm font-bold text-gray-900">{n.title}</p>
                            <p className="text-xs text-gray-600 mt-0.5">{n.message}</p>
                            <p className="text-[10px] text-gray-400 mt-2 font-medium">{n.time}</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="p-3 text-center bg-gray-50/50">
                    <button className="text-xs font-bold text-blue-600 hover:text-blue-700">Mark all as read</button>
                  </div>
                </motion.div>
              </>
            )}
          </AnimatePresence>
        </div>
        
        <button onClick={() => navigate('/optimizer')} className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-medium shadow-sm transition-colors">
          New Optimization
        </button>
        <button onClick={handleLogout} className="text-gray-400 hover:text-red-500 transition-colors p-2">
          <SafeIcon icon={FiLogOut} className="text-xl" title="Logout" />
        </button>
      </div>
    </header>
  );
};

export default Header;